import { useEffect, useState, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getOrders, bulkDeleteOrders } from '../../services/orderService'
import type { OrderSummaryResponse, OrderStatus } from '../../types'
import OrderStatusBadge from '../../components/ui/OrderStatusBadge'
import './OrdersPage.css'

const ORDER_STATUSES: OrderStatus[] = [
  'CREATED', 'AI_PROCESSING', 'AI_PROCESSED', 'AI_ERROR', 'REPORT_SENT',
]

const PAGE_SIZE = 20
const MAX_BULK_DELETE = 100

export default function OrdersPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()

  const [orders, setOrders] = useState<OrderSummaryResponse[]>([])
  const [totalPages, setTotalPages] = useState(0)
  const [totalElements, setTotalElements] = useState(0)
  const [loading, setLoading] = useState(true)

  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [deleteModal, setDeleteModal] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [toast, setToast] = useState<string | null>(null)

  const status = (searchParams.get('status') as OrderStatus) || ''
  const page = parseInt(searchParams.get('page') || '0', 10)
  const archived = searchParams.get('archived') === 'true'
  const from = searchParams.get('from') || ''
  const to = searchParams.get('to') || ''

  const load = useCallback(async (targetPage = page) => {
    setLoading(true)
    try {
      const result = await getOrders({
        status: status as OrderStatus || undefined,
        from: from || undefined,
        to: to || undefined,
        archived: archived || undefined,
        page: targetPage,
        size: PAGE_SIZE,
      })
      setOrders(result.content)
      setTotalPages(result.totalPages)
      setTotalElements(result.totalElements)
    } catch (err) {
      console.error('[ERROR] Failed to load orders', err)
    } finally {
      setLoading(false)
    }
  }, [status, from, to, archived, page])

  useEffect(() => {
    setSelected(new Set())
    load()
  }, [load])

  function showToast(msg: string) {
    setToast(msg)
    setTimeout(() => setToast(null), 5000)
  }

  function setParam(key: string, value: string) {
    const next = new URLSearchParams(searchParams)
    if (value) next.set(key, value)
    else next.delete(key)
    next.delete('page')
    setSearchParams(next)
  }

  function setPage(p: number) {
    const next = new URLSearchParams(searchParams)
    if (p === 0) next.delete('page')
    else next.set('page', String(p))
    setSearchParams(next)
  }

  // ─── Selection ───────────────────────────────────────────────────────────────

  const allOnPageSelected = orders.length > 0 && orders.every(o => selected.has(o.orderNumber))

  function toggleRow(orderNumber: string) {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(orderNumber)) next.delete(orderNumber)
      else next.add(orderNumber)
      return next
    })
  }

  function toggleAll() {
    if (allOnPageSelected) {
      setSelected(new Set())
    } else {
      setSelected(new Set(orders.map(o => o.orderNumber)))
    }
  }

  // ─── Bulk delete ─────────────────────────────────────────────────────────────

  const selectedCount = selected.size
  const overLimit = selectedCount > MAX_BULK_DELETE

  async function handleConfirmDelete() {
    setDeleteLoading(true)
    try {
      const { deletedCount } = await bulkDeleteOrders([...selected])
      const skipped = selectedCount - deletedCount

      setSelected(new Set())
      setDeleteModal(false)

      if (deletedCount === 0) {
        showToast(t('admin.orders.toast_none_deleted'))
      } else if (skipped > 0) {
        showToast(t('admin.orders.toast_deleted_with_skip', { deleted: deletedCount, skipped }))
      } else {
        showToast(t('admin.orders.toast_deleted', { deleted: deletedCount }))
      }

      // Edge case 6: if current page became empty, go to prev page
      const remainingOnPage = orders.filter(o => !selected.has(o.orderNumber)).length + (selectedCount - deletedCount)
      if (remainingOnPage === 0 && page > 0) {
        setPage(page - 1)
      } else {
        load()
      }
    } catch (err) {
      console.error('[ERROR] Bulk delete failed', err)
      setDeleteModal(false)
      showToast(t('admin.orders.toast_delete_failed'))
    } finally {
      setDeleteLoading(false)
    }
  }

  return (
    <div className="orders-page">
      {toast && <div className="orders-toast">{toast}</div>}

      <div className="orders-page__header">
        <h1 className="orders-page__title">{t('admin.orders.title')}</h1>
        <span className="orders-page__count">{t('admin.orders.total', { count: totalElements })}</span>
      </div>

      {/* Filters */}
      <div className="orders-page__filters">
        <select
          className="admin-select"
          value={status}
          onChange={(e) => setParam('status', e.target.value)}
        >
          <option value="">{t('admin.orders.all_statuses')}</option>
          {ORDER_STATUSES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>

        <input
          className="admin-input"
          type="datetime-local"
          value={from}
          onChange={(e) => setParam('from', e.target.value)}
          title="From date"
        />
        <input
          className="admin-input"
          type="datetime-local"
          value={to}
          onChange={(e) => setParam('to', e.target.value)}
          title="To date"
        />

        <label className="admin-checkbox">
          <input
            type="checkbox"
            checked={archived}
            onChange={(e) => setParam('archived', e.target.checked ? 'true' : '')}
          />
          {t('admin.orders.show_archived')}
        </label>
      </div>

      {/* Bulk action panel */}
      {selectedCount > 0 && (
        <div className="orders-bulk-panel">
          <span className="orders-bulk-panel__count">{t('admin.orders.selected_count', { count: selectedCount })}</span>
          <div className="orders-bulk-panel__actions">
            {overLimit && (
              <span className="orders-bulk-panel__limit-warn">{t('admin.orders.max_bulk_warn')}</span>
            )}
            <button
              className="admin-btn admin-btn--danger"
              disabled={overLimit}
              onClick={() => setDeleteModal(true)}
            >
              {t('admin.orders.delete_selected')}
            </button>
            <button
              className="admin-btn admin-btn--secondary"
              onClick={() => setSelected(new Set())}
            >
              {t('admin.orders.clear_selection')}
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="admin-table">
        <div className="admin-table__head orders-page__cols">
          <span className="orders-page__checkbox-cell">
            <input
              type="checkbox"
              checked={allOnPageSelected}
              ref={el => { if (el) el.indeterminate = selectedCount > 0 && !allOnPageSelected }}
              onChange={toggleAll}
              disabled={loading || orders.length === 0}
              aria-label="Select all on page"
            />
          </span>
          <span>{t('admin.orders.col_order')}</span>
          <span>{t('admin.orders.col_car')}</span>
          <span>{t('admin.orders.col_client')}</span>
          <span>{t('admin.orders.col_mechanic')}</span>
          <span>{t('admin.orders.col_date')}</span>
          <span>{t('admin.orders.col_status')}</span>
          <span>{t('admin.orders.col_ai')}</span>
          <span>{t('admin.orders.col_retries')}</span>
        </div>

        {loading ? (
          <div className="admin-table__empty">
            <div className="admin-spinner" />
          </div>
        ) : orders.length === 0 ? (
          <div className="admin-table__empty">{t('admin.orders.no_orders')}</div>
        ) : (
          orders.map((o) => {
            const isSelected = selected.has(o.orderNumber)
            return (
              <div
                key={o.id}
                className={`admin-table__row orders-page__cols${o.archivedAt ? ' admin-table__row--archived' : ''}${isSelected ? ' orders-page__row--selected' : ''}`}
                onClick={() => navigate(`/admin/orders/${o.orderNumber}`)}
              >
                <span
                  className="orders-page__checkbox-cell"
                  onClick={e => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleRow(o.orderNumber)}
                    aria-label={`Select order ${o.orderNumber}`}
                  />
                </span>
                <span className="admin-table__primary">{o.orderNumber}</span>
                <span>
                  <span className="admin-table__primary">{o.carModel}</span>
                  <span className="admin-table__secondary">{o.vin || o.carNumber}</span>
                </span>
                <span>
                  <span className="admin-table__primary">{o.clientName}</span>
                  <span className="admin-table__secondary">{o.clientPhone}</span>
                </span>
                <span className="admin-table__secondary">{o.mechanicName}</span>
                <span className="admin-table__secondary">{formatDate(o.dateTimeTs)}</span>
                <span><OrderStatusBadge status={o.status} /></span>
                <span>
                  <AiStatusDot yolo={o.yoloStatus} tts={o.ttsStatus} />
                </span>
                <span className={o.retryCount > 0 ? 'orders-page__retries--used' : 'admin-table__secondary'}>
                  {o.retryCount}/4
                </span>
              </div>
            )
          })
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="admin-pagination">
          <button
            className="admin-pagination__btn"
            disabled={page === 0}
            onClick={() => setPage(page - 1)}
          >
            {t('admin.orders.prev')}
          </button>
          <span className="admin-pagination__info">
            {t('admin.orders.page_info', { page: page + 1, total: totalPages })}
          </span>
          <button
            className="admin-pagination__btn"
            disabled={page >= totalPages - 1}
            onClick={() => setPage(page + 1)}
          >
            {t('admin.orders.next')}
          </button>
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteModal && (
        <div className="orders-modal-overlay" onClick={() => !deleteLoading && setDeleteModal(false)}>
          <div className="orders-modal" onClick={e => e.stopPropagation()}>
            <h3>{t('admin.orders.delete_title')}</h3>
            <p>{t('admin.orders.delete_confirm', { count: selectedCount })}</p>
            <div className="orders-modal__actions">
              <button
                className="admin-btn admin-btn--secondary"
                onClick={() => setDeleteModal(false)}
                disabled={deleteLoading}
              >
                {t('admin.orders.cancel')}
              </button>
              <button
                className="admin-btn admin-btn--danger"
                onClick={handleConfirmDelete}
                disabled={deleteLoading}
              >
                {deleteLoading ? t('admin.orders.deleting') : t('admin.orders.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function AiStatusDot({ yolo, tts }: { yolo: string; tts: string }) {
  const ok = yolo === 'SUCCESS' && tts === 'SUCCESS'
  const err = yolo === 'FAILED' || tts === 'FAILED'
  const color = err ? '#ef4444' : ok ? '#10b981' : '#f59e0b'
  return (
    <span
      title={`YOLO: ${yolo} / TTS: ${tts}`}
      style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: color }}
    />
  )
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}
